/**
 * The one place a stored or transmitted timestamp becomes a `Date`, for the
 * client (real and mock services) and the Cloud Functions alike. A `Date`
 * passes through, anything with a `toDate()` (a Firestore `Timestamp` from the
 * client SDK or the Admin SDK) is unwrapped, and an ISO string or epoch
 * milliseconds is parsed. `fallback` stands in for a missing (`null` /
 * `undefined`) value; without one, a missing value is an Invalid Date, as
 * `new Date(undefined)` would be.
 */
export function toDate(value, fallback) {
    if (value instanceof Date)
        return value;
    if ((value === undefined || value === null) && fallback)
        return fallback;
    if (value && typeof value.toDate === "function") {
        return value.toDate();
    }
    return new Date(value);
}
/** A raw tick-checkpoint event with every timestamp on it, and on its details, as a real `Date`. */
export function normalizeEvent(raw) {
    const event = raw;
    const details = (event.eventDetails ?? {});
    return {
        ...event,
        createdDate: toDate(event.createdDate),
        updatedDate: toDate(event.updatedDate),
        endDate: toDate(event.endDate),
        eventDetails: {
            ...details,
            ...(details.startDate !== undefined ? { startDate: toDate(details.startDate) } : {}),
            // Robots grouped shipBuild batches (issue #91) hold delivery until
            // this later timestamp — optional, and the engine's
            // `checkpointTime` calls `.getTime()` on it.
            ...(details.deliverAt !== undefined ? { deliverAt: toDate(details.deliverAt) } : {}),
        },
    };
}
/**
 * A raw planet — Firestore doc data (Timestamps), a Cloud Function response
 * (ISO strings) or a mock's localStorage JSON — with every timestamp on it
 * as a real `Date`. Dates only: the client layers its plot-id backfill on top
 * (`apps/client/src/services/planetNormalization.ts`), and the Cloud Functions
 * backfill when they build a response.
 *
 * Every copy of this that used to live in a separate service drifted: one
 * skipped `tickCheckpoints` (PlanetSurface crashed on `endDate.getTime`),
 * another skipped `lastSpyScanAt` (the spy-scan cooldown crashed the Sciences
 * page, issue #168).
 */
export function normalizePlanet(raw, planetId) {
    const data = raw;
    const state = (data.state ?? {});
    const scrapRecycleDaily = data.scrapRecycleDaily;
    const resourcePurchaseDaily = data.resourcePurchaseDaily;
    const tradeWindow = data.tradeWindow;
    return {
        ...data,
        id: planetId,
        ...(data.createdAt !== undefined ? { createdAt: toDate(data.createdAt) } : {}),
        ...(data.updatedAt !== undefined ? { updatedAt: toDate(data.updatedAt) } : {}),
        state: { ...state, lastTickAt: toDate(state.lastTickAt) },
        tickCheckpoints: (data.tickCheckpoints ?? []).map(normalizeEvent),
        nextEventId: data.nextEventId ?? 1,
        ...(scrapRecycleDaily
            ? { scrapRecycleDaily: { ...scrapRecycleDaily, resetAt: toDate(scrapRecycleDaily.resetAt) } }
            : {}),
        ...(resourcePurchaseDaily
            ? { resourcePurchaseDaily: { ...resourcePurchaseDaily, resetAt: toDate(resourcePurchaseDaily.resetAt) } }
            : {}),
        ...(data.lastSpyScanAt ? { lastSpyScanAt: toDate(data.lastSpyScanAt) } : {}),
        ...(tradeWindow
            ? { tradeWindow: { ...tradeWindow, windowStart: toDate(tradeWindow.windowStart) } }
            : {}),
    };
}
//# sourceMappingURL=normalize.js.map